/**
 * ⚠️ Backend Source of Truth.
 * The `value` strings here MUST match the Pydantic Literal types in
 * dental_crm/app/schemas/visit.py. Labels are display-only.
 */

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
]

export const PAIN_TYPE_OPTIONS = [
  { value: 'sharp', label: 'Sharp' },
  { value: 'dull', label: 'Dull' },
  { value: 'throbbing', label: 'Throbbing' },
]

export const SWELLING_FEVER_OPTIONS = [
  { value: 'swelling', label: 'Swelling' },
  { value: 'fever', label: 'Fever' },
  { value: 'pus_discharge', label: 'Pus discharge' },
]

export const MEDICAL_CONDITION_OPTIONS = [
  { value: 'diabetes', label: 'Diabetes' },
  { value: 'hypertension', label: 'Hypertension' },
  { value: 'thyroid_disorder', label: 'Thyroid disorder' },
  { value: 'asthma', label: 'Asthma' },
  { value: 'heart_disease', label: 'Heart disease' },
  { value: 'bleeding_disorders', label: 'Bleeding disorders' },
  { value: 'pregnancy_lactation', label: 'Pregnancy / Lactation' },
]

export const HABIT_OPTIONS = [
  { value: 'smoking', label: 'Smoking' },
  { value: 'tobacco_chewing', label: 'Tobacco chewing' },
  { value: 'alcohol', label: 'Alcohol' },
]

export const DIET_OPTIONS = [
  { value: 'veg', label: 'Vegetarian' },
  { value: 'non_veg', label: 'Non-vegetarian' },
  { value: 'mixed', label: 'Mixed' },
]

export const MOUTHWASH_FLOSS_OPTIONS = [
  { value: 'mouthwash', label: 'Mouthwash' },
  { value: 'floss', label: 'Floss' },
]

export const INVESTIGATION_OPTIONS = [
  { value: 'iopa', label: 'IOPA' },
  { value: 'opg', label: 'OPG' },
  { value: 'cbct', label: 'CBCT' },
  { value: 'pulp_vitality_test', label: 'Pulp vitality test' },
  { value: 'blood_investigations', label: 'Blood investigations' },
]

/* ---------------- Intra-oral radio options (Phase 3, updated) -------------- */
export const ORAL_HYGIENE_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'medium', label: 'Medium' },
  { value: 'poor', label: 'Poor' },
]

export const GINGIVA_OPTIONS = [
  { value: 'not_bleeding', label: 'Not bleeding' },
  { value: 'bleeding', label: 'Bleeding' },
]

export const RECESSION_OPTIONS = [
  { value: 'grade_1', label: 'Grade 1' },
  { value: 'grade_2', label: 'Grade 2' },
  { value: 'grade_3', label: 'Grade 3' },
]

export const CALCULUS_STAIN_OPTIONS = [
  { value: '1', label: 'Grade 1' },
  { value: '2', label: 'Grade 2' },
  { value: '3', label: 'Grade 3' },
]

/* ---------------- Treatment plan multi-select (Phase 4, updated) ---------- */
export const TREATMENT_OPTIONS = [
  { value: 'scaling', label: 'Scaling' },
  { value: 'filling', label: 'Filling' },
  { value: 'rct', label: 'Root canal treatment (RCT)' },
  { value: 'extraction', label: 'Extraction' },
  { value: 'implant', label: 'Implant' },
  { value: 'rpd', label: 'Removable Partial Denture (RPD)' },
  { value: 'cd', label: 'Complete Denture (CD)' },
  { value: 'bleaching', label: 'Bleaching' },
  { value: 'veneers', label: 'Veneers' },
  { value: 'splint', label: 'Splint' },
  { value: 'fluoride_therapy', label: 'Fluoride therapy' },
]

/* ---------------- FDI Tooth numbering ------------------------------------- */
/** Adult / permanent dentition - 32 teeth in 4 quadrants. */
export const ADULT_QUADRANTS = {
  upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
  upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
  lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
  lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
}
export const ADULT_TEETH = [
  ...ADULT_QUADRANTS.upperRight,
  ...ADULT_QUADRANTS.upperLeft,
  ...ADULT_QUADRANTS.lowerRight,
  ...ADULT_QUADRANTS.lowerLeft,
]

/** Deciduous (child) dentition - 20 teeth in 4 quadrants. */
export const CHILD_QUADRANTS = {
  upperRight: [55, 54, 53, 52, 51],
  upperLeft: [61, 62, 63, 64, 65],
  lowerRight: [85, 84, 83, 82, 81],
  lowerLeft: [71, 72, 73, 74, 75],
}
export const CHILD_TEETH = [
  ...CHILD_QUADRANTS.upperRight,
  ...CHILD_QUADRANTS.upperLeft,
  ...CHILD_QUADRANTS.lowerRight,
  ...CHILD_QUADRANTS.lowerLeft,
]

/* ---------------- Clinic branding ----------------------------------------- */
export const CLINIC = {
  name: import.meta.env.VITE_CLINIC_NAME || 'Radiance Dental Clinic',
  tagline: import.meta.env.VITE_CLINIC_TAGLINE || 'Radiate a healthy smile',
  address:
    import.meta.env.VITE_CLINIC_ADDRESS ||
    'Plot No 2, TKR Complex 1st Floor, TWAD Colony First Street, Thirupalai, Madurai',
  phone: import.meta.env.VITE_CLINIC_PHONE || '9677893709 / 9952761489',
  email: import.meta.env.VITE_CLINIC_EMAIL || 'dr.radiancedental@gmail.com',
  gstin: import.meta.env.VITE_CLINIC_GSTIN || '33BSWPB5044G1ZR',
}

/* ---------------- Section ↔ URL slug mapping ------------------------------ */
export const VISIT_SECTION_SLUGS = {
  vitals: 'vitals',
  history_present_illness: 'history-present-illness',
  medical_history: 'medical-history',
  personal_history: 'personal-history',
  chief_complaint: 'chief-complaint',
  dental_history: 'dental-history',
  extra_oral_examination: 'extra-oral-examination',
  intra_oral_examination: 'intra-oral-examination',
  investigation: 'investigation',
  provisional_diagnosis: 'provisional-diagnosis',
  treatment_plan: 'treatment-plan',
  prescription: 'prescription',
  consent: 'consent',
  doctor_notes: 'doctor-notes',
}

export const VISIT_SECTION_LABELS = {
  vitals: 'Vitals',
  history_present_illness: 'History of Present Illness',
  medical_history: 'Medical History',
  personal_history: 'Personal History',
  chief_complaint: 'Chief Complaint',
  dental_history: 'Dental History',
  extra_oral_examination: 'Extra Oral Examination',
  intra_oral_examination: 'Intra Oral Examination',
  investigation: 'Investigation',
  provisional_diagnosis: 'Provisional Diagnosis',
  treatment_plan: 'Treatment Plan',
  prescription: 'Prescription',
  consent: 'Consent',
  doctor_notes: 'Doctor Notes',
}

/* ---------------- Phase 6 — Drug list (inventory) ------------------------- */
export const DRUG_CATEGORY_OPTIONS = [
  { value: 'tablet',       label: 'Tablet' },
  { value: 'antibiotics',  label: 'Antibiotics' },
  { value: 'pain_killers', label: 'Pain killers' },
  { value: 'gel',          label: 'Gel' },
  { value: 'paste',        label: 'Paste' },
  { value: 'mouthwash',    label: 'Mouthwash' },
  { value: 'brush_adult',  label: 'Brush — Adult' },
  { value: 'brush_kids',   label: 'Brush — Kids' },
  { value: 'ortho',        label: 'Ortho' },
]

export const DRUG_CATEGORY_LABEL = Object.fromEntries(
  DRUG_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
)

export const STOCK_FILTER_TABS = [
  { value: 'all',           label: 'All',           tone: 'primary' },
  { value: 'needs_restock', label: 'Out of stock',  tone: 'danger' },
  { value: 'low_stock',     label: 'Low stock',     tone: 'accent' },
  { value: 'near_expiry',   label: 'Near expiry',   tone: 'accent' },
  { value: 'expired',       label: 'Expired',       tone: 'danger' },
  { value: 'slow_moving',   label: 'Slow moving',   tone: 'neutral' },
]

/* ---------------- Phase 7 — Billing --------------------------------------- */
export const BILL_STATUS_OPTIONS = [
  { value: 'active',    label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
]

export const STOCK_MOVEMENT_LABEL = {
  in: 'Stock in',
  out: 'Dispensed',
  restore: 'Restored',
  adjust: 'Adjusted',
}

export const STOCK_MOVEMENT_TONE = {
  in: 'success',
  out: 'primary',
  restore: 'accent',
  adjust: 'neutral',
}

/* ---------------- App settings -------------------------------------------- */
export const SETTING_META = {
  near_expiry_days: {
    label: 'Near-expiry window',
    description: 'Drugs expiring within this many days are flagged as "Near expiry".',
    suffix: 'days',
    inputType: 'number',
    min: 1,
  },
  low_stock_default: {
    label: 'Low-stock default threshold',
    description: 'Used when a drug does not have its own reorder level.',
    suffix: 'units',
    inputType: 'number',
    min: 0,
  },
  slow_moving_days: {
    label: 'Slow-moving window',
    description: 'Drugs not dispensed in this many days are flagged as "Slow moving".',
    suffix: 'days',
    inputType: 'number',
    min: 1,
  },
}

/* ---------------- Visit form step config ---------------------------------- */
export const VISIT_STEPS = [
  { key: 'vitals', label: 'Vitals', short: 'Vitals' },
  { key: 'medical_history', label: 'Medical History', short: 'Medical' },
  { key: 'personal_history', label: 'Personal History', short: 'Personal' },
  { key: 'chief_complaint', label: 'Chief Complaint', short: 'Complaint' },
  { key: 'history_present_illness', label: 'Present Illness', short: 'Illness' },
  { key: 'dental_history', label: 'Dental History', short: 'Dental' },
  { key: 'intra_oral_examination', label: 'Intra Oral', short: 'Intra Oral' },
  { key: 'extra_oral_examination', label: 'Extra Oral', short: 'Extra Oral' },
  { key: 'investigation', label: 'Investigation', short: 'Invest.' },
  { key: 'provisional_diagnosis', label: 'Diagnosis', short: 'Diagnosis' },
  { key: 'treatment_plan', label: 'Treatment Plan', short: 'Plan' },
  { key: 'prescription', label: 'Prescription', short: 'Rx' },
  { key: 'consent', label: 'Consent', short: 'Consent' },
  { key: 'doctor_notes', label: 'Doctor Notes', short: 'Notes' },
]
